/**
 * Access levels and the pure comparison helpers.
 *
 * These functions are the ONLY place level comparison is implemented. The Nest
 * `PermissionsGuard` and the React route guard both call them, so a frontend
 * check can never drift from the backend check. The frontend use is additive
 * (hide what you cannot use); the backend use is authoritative.
 */
export const ACCESS_LEVELS = ['NONE', 'VIEW', 'FULL'];
export function isAccessLevel(value) {
    return typeof value === 'string' && ACCESS_LEVELS.includes(value);
}
/** NONE=0 < VIEW=1 < FULL=2. Ordering is the whole point; keep it total. */
const RANK = Object.freeze({
    NONE: 0,
    VIEW: 1,
    FULL: 2,
});
export function rank(level) {
    return RANK[level];
}
/**
 * Anything not recognisable as a level resolves to NONE.
 * A corrupt or unknown value must deny, never default open.
 */
export function normalizeLevel(value) {
    return isAccessLevel(value) ? value : 'NONE';
}
export function satisfies(effective, required) {
    return rank(effective) >= rank(required);
}
export function canRead(level) {
    return satisfies(normalizeLevel(level), 'VIEW');
}
export function canWrite(level) {
    return satisfies(normalizeLevel(level), 'FULL');
}
/**
 * Default level demanded by an HTTP verb when a route does not state one.
 *
 * Safe verbs need VIEW; everything else needs FULL. Note the fallback: an
 * unknown or malformed method demands FULL rather than VIEW, so a novel verb
 * cannot slip in under a read-level check.
 */
export function requiredLevelForMethod(method) {
    const safe = ['GET', 'HEAD', 'OPTIONS'];
    return safe.includes(String(method).toUpperCase()) ? 'VIEW' : 'FULL';
}
//# sourceMappingURL=access.js.map